import { Injectable } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  LoaderDebugEntry,
  LoaderDebugSnapshot,
} from '../models/loading-debug.models';
import {
  ActiveLoaderRecord,
  LoaderMode,
  LoaderScope,
  LoaderScopeType,
} from '../models/loading.models';
import { LoaderOrchestratorService } from './loader-orchestrator.service';
import { LoaderRegistryService } from './loader-registry.service';

const TRACKED_LOADER_MODES: LoaderMode[] = [
  'fullscreen-blocking',
  'section-blocking',
  'top-bar',
  'inline-nonblocking',
];

const TRACKED_SCOPE_TYPES: LoaderScopeType[] = [
  'global',
  'route',
  'module',
  'section',
  'component',
  'item',
  'request-group',
];

@Injectable({
  providedIn: 'root',
})
export class LoaderDebugService {
  readonly enabled = Boolean(environment.loadingDebug);

  readonly activeLoaderCount$: Observable<number> = this.loaderRegistryService
    .selectAllActiveRecords()
    .pipe(
      map((activeLoaderRecords) => activeLoaderRecords.length),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  readonly snapshot$: Observable<LoaderDebugSnapshot> = combineLatest([
    this.loaderRegistryService.selectAllActiveRecords(),
    this.loaderRegistryService.selectActiveLoaderGroups(),
    this.loaderRegistryService.selectActiveFullscreenLoader(),
    this.loaderRegistryService.selectTopBarVisible(),
    this.loaderRegistryService.selectAllVisibleSectionLoaders(),
  ]).pipe(
    map(
      ([
        activeLoaderRecords,
        activeLoaderGroups,
        activeFullscreenLoader,
        topBarVisible,
        visibleSectionLoaders,
      ]) => {
        void activeLoaderGroups;

        const generatedAtEpochMs = Date.now();
        const visibleSectionTokenIds = new Set(
          visibleSectionLoaders.map(
            (visibleSectionLoader) => visibleSectionLoader.dominantRecord.token.id
          )
        );

        return {
          enabled: this.enabled,
          generatedAtEpochMs,
          entries: activeLoaderRecords.map((activeLoaderRecord) =>
            this.buildDebugEntry(
              activeLoaderRecord,
              generatedAtEpochMs,
              Boolean(activeFullscreenLoader),
              visibleSectionTokenIds
            )
          ),
          countsByMode: this.buildCountsByMode(activeLoaderRecords),
          countsByScopeType: this.buildCountsByScopeType(activeLoaderRecords),
          activeFullscreen: Boolean(activeFullscreenLoader),
          activeTopBar: topBarVisible,
          activeSectionCount: visibleSectionLoaders.length,
        } satisfies LoaderDebugSnapshot;
      }
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly loaderRegistryService: LoaderRegistryService,
    private readonly loaderOrchestratorService: LoaderOrchestratorService
  ) {}

  clearScope(loaderScope: LoaderScope): void {
    this.loaderOrchestratorService.clearScope(loaderScope);
  }

  clearGroup(groupId: string): void {
    this.loaderOrchestratorService.clearGroup(groupId);
  }

  clearAll(): void {
    this.loaderOrchestratorService.clearAll();
  }

  private buildDebugEntry(
    activeLoaderRecord: ActiveLoaderRecord,
    generatedAtEpochMs: number,
    hasVisibleFullscreenLoader: boolean,
    visibleSectionTokenIds: Set<string>
  ): LoaderDebugEntry {
    const visibleDurationMs = activeLoaderRecord.visibleAtEpochMs
      ? Math.max(generatedAtEpochMs - activeLoaderRecord.visibleAtEpochMs, 0)
      : 0;

    return {
      tokenId: activeLoaderRecord.token.id,
      mode: activeLoaderRecord.visibleMode,
      scopeType: activeLoaderRecord.config.scope.type,
      scopeKey: activeLoaderRecord.config.scope.key,
      ownerKey: `${activeLoaderRecord.config.owner.type}:${activeLoaderRecord.config.owner.name}`,
      groupId: activeLoaderRecord.config.groupId,
      referenceKey: activeLoaderRecord.config.referenceKey,
      status: activeLoaderRecord.status,
      startedAtEpochMs: activeLoaderRecord.startedAtEpochMs,
      ageMs: Math.max(generatedAtEpochMs - activeLoaderRecord.startedAtEpochMs, 0),
      visibleDurationMs,
      resolvedPriority: activeLoaderRecord.resolvedPriority,
      visuallySuppressed: this.isVisuallySuppressed(
        activeLoaderRecord,
        hasVisibleFullscreenLoader,
        visibleSectionTokenIds
      ),
    };
  }

  private buildCountsByMode(
    activeLoaderRecords: ActiveLoaderRecord[]
  ): Record<LoaderMode, number> {
    return TRACKED_LOADER_MODES.reduce<Record<LoaderMode, number>>(
      (countsByMode, loaderMode) => {
        countsByMode[loaderMode] = activeLoaderRecords.filter(
          (activeLoaderRecord) => activeLoaderRecord.visibleMode === loaderMode
        ).length;

        return countsByMode;
      },
      {
        'fullscreen-blocking': 0,
        'section-blocking': 0,
        'top-bar': 0,
        'inline-nonblocking': 0,
      }
    );
  }

  private buildCountsByScopeType(
    activeLoaderRecords: ActiveLoaderRecord[]
  ): Record<LoaderScopeType, number> {
    return TRACKED_SCOPE_TYPES.reduce<Record<LoaderScopeType, number>>(
      (countsByScopeType, loaderScopeType) => {
        countsByScopeType[loaderScopeType] = activeLoaderRecords.filter(
          (activeLoaderRecord) =>
            activeLoaderRecord.config.scope.type === loaderScopeType
        ).length;

        return countsByScopeType;
      },
      {
        global: 0,
        route: 0,
        module: 0,
        section: 0,
        component: 0,
        item: 0,
        'request-group': 0,
      }
    );
  }

  private isVisuallySuppressed(
    activeLoaderRecord: ActiveLoaderRecord,
    hasVisibleFullscreenLoader: boolean,
    visibleSectionTokenIds: Set<string>
  ): boolean {
    if (activeLoaderRecord.visibleMode === 'fullscreen-blocking') {
      return false;
    }

    if (activeLoaderRecord.visibleMode === 'top-bar') {
      return hasVisibleFullscreenLoader;
    }

    if (activeLoaderRecord.visibleMode === 'section-blocking') {
      return (
        hasVisibleFullscreenLoader ||
        !visibleSectionTokenIds.has(activeLoaderRecord.token.id)
      );
    }

    return false;
  }
}
